import {
	FileUnreadableError,
	probeCloudHostedFile,
	withPickedFileRead,
	type ChunkSourceFactory,
	type PersistableChunkSource
} from "@uploads";
import type { FileUploadSession } from "@/db/fileUploadDb";
import { uploadFailureLogDetails } from "./uploadLogRedaction";
import { adminUploadTelemetry } from "./adminUploadTelemetry";

export { FileUnreadableError };

// The three DOMException names are the same "can't produce this file's bytes" signal on different
// platforms: NotReadableError/SecurityError (Android cloud-offload + stale handles) and NotFoundError
// (the iOS/macOS iCloud "Optimize Storage" variant — a picked HEIC/photo offloaded to iCloud raises
// "A requested file or directory could not be found..." on read; RUM shows this on iOS Safari AND
// macOS Chrome). Retrying the same File handle can never succeed for any of them (LSP-16961).
export function isFileReadError(error: unknown): boolean {
	if (error instanceof FileUnreadableError) return true;
	return (
		error instanceof Error && (error.name === "NotReadableError" || error.name === "SecurityError" || error.name === "NotFoundError")
	);
}

async function readSliceAsArrayBuffer(file: File, start: number, end: number): Promise<ArrayBuffer> {
	try {
		return await withPickedFileRead(() => file.slice(start, end).arrayBuffer());
	} catch (readError) {
		if (isFileReadError(readError)) throw new FileUnreadableError(file.name);
		throw readError;
	}
}

async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
	try {
		return await withPickedFileRead(() => file.arrayBuffer());
	} catch (readError) {
		if (isFileReadError(readError)) throw new FileUnreadableError(file.name);
		throw readError;
	}
}

export function createAdminChunkSourceFactory(options: {
	chunkSize: number;
	cloudProbeTimeoutMs: number;
	readChunkData(session: FileUploadSession, chunkIndex: number): Promise<ArrayBuffer | undefined>;
	deleteAllChunkData(session: FileUploadSession): Promise<void>;
}): ChunkSourceFactory<File, FileUploadSession> {
	const readLiveBuffer = (file: File, chunkIndex: number) =>
		readSliceAsArrayBuffer(file, chunkIndex * options.chunkSize, Math.min((chunkIndex + 1) * options.chunkSize, file.size));

	const createPersistenceSource = (file: File, wholeFile?: ArrayBuffer): PersistableChunkSource => ({
		async readBuffer(chunkIndex) {
			try {
				const buffer = wholeFile
					? wholeFile.slice(chunkIndex * options.chunkSize, Math.min((chunkIndex + 1) * options.chunkSize, file.size))
					: await readLiveBuffer(file, chunkIndex);
				return buffer.byteLength > 0 ? { ok: true, buffer } : { ok: false, kind: "missing" };
			} catch (error) {
				if (error instanceof FileUnreadableError) return { ok: false, kind: "unreadable" };
				throw error;
			}
		},
		async read(chunkIndex, signal) {
			const result = await this.readBuffer(chunkIndex, signal);
			return result.ok ? { ok: true, blob: new Blob([result.buffer]) } : result;
		}
	});

	return {
		detectCloudHosted(file, context) {
			return probeCloudHostedFile(file, options.cloudProbeTimeoutMs, {
				onSettled: (observation) =>
					adminUploadTelemetry.logInfo("recording.fileUpload.sourceProbe", {
						...context,
						...observation,
						fileSize: file.size,
						expectedChunkCount: Math.ceil(file.size / options.chunkSize)
					}),
				onTimeout: () => {
					console.warn(
						"[Upload] One-byte source read timed out; selecting slow-source handling without confirmed cloud provenance",
						{
							timeoutMs: options.cloudProbeTimeoutMs
						}
					);
				},
				onReadError: (probeError) => {
					console.warn("[Upload] cloud-host probe read threw; file may be unreadable", uploadFailureLogDetails(probeError));
				}
			});
		},
		readWholeFile: (file) => readFileAsArrayBuffer(file),
		forPersistence: createPersistenceSource,
		forDirectUpload({ file, session, cloudHosted }) {
			return {
				async read(chunkIndex) {
					let persistedReadFailure: { error: unknown } | undefined;
					const readPersisted = async (): Promise<ArrayBuffer | undefined> => {
						if (!session) return undefined;
						try {
							return await options.readChunkData(session, chunkIndex);
						} catch (idbReadError) {
							persistedReadFailure = { error: idbReadError };
							try {
								console.warn("[Upload] IDB read failed; falling back to live file reads", {
									sessionId: session.id,
									chunkIndex,
									...uploadFailureLogDetails(idbReadError)
								});
							} catch {
								// A diagnostic failure must not replace the usable live-file fallback.
							}
							return undefined;
						}
					};

					if (cloudHosted) {
						const persisted = await readPersisted();
						if (persisted?.byteLength) return { ok: true, blob: new Blob([persisted]) };
					}
					try {
						const liveBuffer = await readLiveBuffer(file, chunkIndex);
						// Cross-app parity: async's createAsyncFileChunkSource.read() refuses a zero-byte
						// slice as "missing" (a live-file bug, not a legitimate chunk); this path lacked the
						// equivalent check and would report a 0-byte read as a successful upload part.
						if (liveBuffer.byteLength === 0) {
							if (persistedReadFailure) throw persistedReadFailure.error;
							return { ok: false, kind: "missing" };
						}
						return { ok: true, blob: new Blob([liveBuffer]) };
					} catch (error) {
						if (!cloudHosted && error instanceof FileUnreadableError) {
							const persisted = await readPersisted();
							if (persisted?.byteLength) return { ok: true, blob: new Blob([persisted]) };
						}
						// A failed read is not proof that the stored bytes are gone. Propagate storage
						// uncertainty so the driver retries instead of cancelling an unreadable live file.
						if (persistedReadFailure) throw persistedReadFailure.error;
						if (error instanceof FileUnreadableError) return { ok: false, kind: session ? "missing" : "unreadable" };
						throw error;
					}
				}
			};
		},
		forPersistedUpload(session) {
			return {
				async read(chunkIndex) {
					const buffer = await options.readChunkData(session, chunkIndex);
					return buffer?.byteLength ? { ok: true, blob: new Blob([buffer]) } : { ok: false, kind: "missing" };
				}
			};
		}
	};
}
